import React, { useState, useEffect, useRef, useCallback } from "react";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { Card, CardContent } from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { rltBoletimItinerarioWeb } from "../../api-relatorio";
import RelatorioBoletimNotaBimestreWeb from "./RelatorioBoletimNotaBimestreWeb";
import RelatorioCabecalhoWeb from "../Cabecalho/RelatorioCabecalhoWeb";
import RelatorioBoletimEletivaWeb from "./RelatorioBoletimEletivaWeb";
import RelatorioBoletimProjetoWeb from "./RelatorioBoletimProjetoWeb";
import SimpleDialogMessage from "../../../../util/SimpleDialogMessage/SimpleDialogMessage";
import { open } from "../../../../util/SimpleDialogMessage/actions/actions";
import { findByTurma } from "../../../AlunoTurma/api-aluno-turma";

const LEGENDA_ITEMS = [
  ["A. C.", "Ausência Compensada"],
  ["Bim.", "Bimestre"],
  ["Con. Final", "Conceito Final"],
  ["C. H.", "Carga Horária"],
  ["Freq.", "Frequência"],
  ["Rec. Int.", "Recuperação Intensiva"],
  ["5º Con.", "5º Conceito"],
];

export default function RelatorioBoletimItinerarioWeb() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { numeroBimestre, codTurma } = location.state;
  const [codAlunosTurmas, setCodAlunosTurmas] = useState(
    location.state.codAlunosTurmas ?? [],
  );

  const [listaBE, setListaBE] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [dialogMessage, setDialogMessage] = useState({
    title: "",
    description: "",
  });

  const progressRef = useRef(0);

  // Busca alunos pela turma, se necessário
  useEffect(() => {
    if (!codTurma) return;

    findByTurma(codTurma).then((data) => {
      if (data.error) {
        console.error("Erro ao buscar alunos da turma:", data);
        return;
      }
      setCodAlunosTurmas(data.map((d) => d.codigo));
    });
  }, [codTurma]);

  // Carrega boletins sequencialmente
  useEffect(() => {
    if (!codAlunosTurmas?.length) return;

    let isMounted = true;
    progressRef.current = 0;

    async function carregarBoletins() {
      const total = codAlunosTurmas.length;

      for (let i = 0; i < total; i++) {
        if (!isMounted) break;

        const data = await rltBoletimItinerarioWeb(
          numeroBimestre,
          codAlunosTurmas[i],
        );

        if (!isMounted) break;

        if (data.error) {
          if (data.message === "More than one turma") {
            setDialogMessage({
              title: "Alerta",
              description:
                "Aluno cadastrado em 2 turmas ou mais. O aluno só pode ser ativo em 1 turma.",
            });
            dispatch(open());
          } else {
            console.error("Erro ao carregar boletim:", data);
          }
        } else {
          setListaBE((prev) => [...prev, ...data]);
        }

        progressRef.current = i + 1;
        setLoadingProgress(Math.round(((i + 1) / total) * 100));
      }

      if (isMounted) setIsLoading(false);
    }

    carregarBoletins();

    return () => {
      isMounted = false;
    };
  }, [numeroBimestre, codAlunosTurmas, dispatch]);

  const handleGoBack = useCallback(() => navigate(-1), [navigate]);

  return (
    <Box>
      <SimpleDialogMessage
        title={dialogMessage.title}
        description={dialogMessage.description}
      />

      <Container
        component={Paper}
        sx={{ padding: "24px", backgroundColor: "white" }}
      >
        {/* Botão Voltar — oculto na impressão */}
        <Button
          onClick={handleGoBack}
          variant="contained"
          color="secondary"
          sx={{ mb: 2, "@media print": { display: "none" } }}
          startIcon={<ArrowBackIcon />}
        >
          Voltar
        </Button>

        {/* Lista de boletins */}
        {listaBE.map((be, index) => (
          <Box key={index} sx={{ mt: 3, mb: 12 }}>
            <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
              Boletim
            </Typography>

            <RelatorioCabecalhoWeb alunoTurma={be.alunoTurma} />

            {be.bnotaBimestres?.length > 0 && (
              <RelatorioBoletimNotaBimestreWeb boletimEscolar={be} />
            )}

            {be.beletivas?.length > 0 && (
              <Box mt={2}>
                <RelatorioBoletimEletivaWeb boletimEscolar={be} />
              </Box>
            )}

            {be.bprojetos?.length > 0 && (
              <Box mt={2}>
                <RelatorioBoletimProjetoWeb boletimEscolar={be} />
              </Box>
            )}

            <LegendaCard />
          </Box>
        ))}

        {/* Estado de carregamento */}
        {isLoading && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <CircularProgress size={20} color="primary" />
              <Typography variant="body2" color="text.secondary">
                Carregando boletins... {loadingProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={loadingProgress} />
          </Box>
        )}

        {/* Estado vazio após carregamento */}
        {!isLoading && listaBE.length === 0 && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
            Nenhum boletim encontrado.
          </Typography>
        )}
      </Container>
    </Box>
  );
}

function LegendaCard() {
  return (
    <Card sx={{ width: 300, mt: 2 }}>
      <CardContent>
        <Typography sx={{ color: "text.secondary", fontSize: 14, mb: 1 }}>
          Legenda
        </Typography>
        {LEGENDA_ITEMS.map(([sigla, descricao]) => (
          <Typography key={sigla} variant="body2" sx={{ fontSize: 12 }}>
            <strong>{sigla}</strong> — {descricao}
          </Typography>
        ))}
      </CardContent>
    </Card>
  );
}
